import { LotOptionRow } from './lot-option-row'
import type {
    PurchaseLot,
    PurchaseLotStockInNoteItem,
} from '@/lib/api/purchase-lot-api'
import type {
    SortingLot,
    SortingLotStockInNoteItem,
} from '@/lib/api/sorting-lot-api'
import type {
    ScentingLot,
    ScentingLotStockInNoteItem,
} from '@/lib/api/scenting-lot-api'
import type {
    PackagingLot,
    PackagingLotStockInNoteItem,
} from '@/lib/api/packaging-lot-api'
import type { LotOption } from './lot-items-field'
import { formatQty } from '@/lib/format'

// Bố cục 2 dòng cho từng loại lô (xem lot-option-row): dòng 1 = mô tả "là gì",
// dòng 2 = mã lô + chip ngắn (trái) · KL khả dụng (phải).

// ---- Chè mua: dòng 1 = hộ dân ----
export function purchaseLotOption(l: PurchaseLot): LotOption {
    return {
        value: l.id,
        label: `${l.code} — ${l.grower_name ?? ''}`,
        available: l.quantity_available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={l.grower_name || l.code}
                code={l.code}
                qty={`${formatQty(l.quantity_available)} kg`}
            />
        ),
    }
}
export function purchaseNoteItemOption(
    it: PurchaseLotStockInNoteItem,
): LotOption {
    const code = it.purchase_lot_code ?? it.purchase_lot_id
    return {
        value: it.purchase_lot_id,
        label: `${code} — ${it.grower_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.grower_name || code}
                code={code}
                qty={`${formatQty(it.quantity)} kg`}
            />
        ),
    }
}

// ---- Phân loại: dòng 1 = loại (grade) ----
export function sortingLotOption(l: SortingLot): LotOption {
    return {
        value: l.id,
        label: `${l.code} — ${l.grade_name ?? ''}`,
        available: l.quantity_available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={l.grade_name || l.code}
                code={l.code}
                qty={`${formatQty(l.quantity_available)} kg`}
            />
        ),
    }
}
export function sortingNoteItemOption(it: SortingLotStockInNoteItem): LotOption {
    const code = it.sorting_lot_code ?? it.sorting_lot_id
    return {
        value: it.sorting_lot_id,
        label: `${code} — ${it.grade_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.grade_name || code}
                code={code}
                qty={`${formatQty(it.quantity)} kg`}
            />
        ),
    }
}

// ---- Lấy hương: dòng 1 = loại (không NSX/HSD) ----
export function scentingLotOption(l: ScentingLot): LotOption {
    return {
        value: l.id,
        label: `${l.code} — ${l.grade_name ?? ''}`,
        available: l.quantity_available,
        unit: 'kg',
        render: () => (
            <LotOptionRow
                title={l.grade_name || l.code}
                code={l.code}
                qty={`${formatQty(l.quantity_available)} kg`}
            />
        ),
    }
}
export function scentingNoteItemOption(
    it: ScentingLotStockInNoteItem,
): LotOption {
    const code = it.scenting_lot_code ?? it.scenting_lot_id
    return {
        value: it.scenting_lot_id,
        label: `${code} — ${it.grade_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.grade_name || code}
                code={code}
                qty={`${formatQty(it.quantity)} kg`}
            />
        ),
    }
}

// ---- Vật tư: dòng 1 = tên vật tư; chip vị trí; đơn vị động ----
export function packagingLotOption(l: PackagingLot): LotOption {
    const unit = l.material_unit ?? ''
    return {
        value: l.id,
        label: `${l.code} — ${l.material_name ?? ''}`,
        available: l.quantity_available,
        unit,
        render: () => (
            <LotOptionRow
                title={l.material_name || l.code}
                code={l.code}
                chips={[l.position_code]}
                qty={`${formatQty(l.quantity_available)}${unit ? ` ${unit}` : ''}`}
            />
        ),
    }
}
export function packagingNoteItemOption(
    it: PackagingLotStockInNoteItem,
): LotOption {
    const code = it.packaging_lot_code ?? it.packaging_lot_id
    return {
        value: it.packaging_lot_id,
        label: `${code} — ${it.material_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.material_name || code}
                code={code}
                chips={[it.position_code]}
                qty={formatQty(it.quantity)}
            />
        ),
    }
}
